import { ShippingMethod } from '@app/types/cart/ShippingMethod';
import { Cart } from '@app/types/monolith/cart';
import { CartQueryShippingAddress } from '@app/types/monolith/types';
import logger from '@redbubble/next/logger';
import { useRef, useState } from 'react';
import type { CartCheckoutClient } from '../../cartCheckoutClient';
import type { ErrorCodeType, StateDispatch } from '../types';
import { DispatchActionsType, ErrorCode } from '../types';
import {
  AsyncHookState,
  AsyncRequestResult,
  StateCode,
} from './AsyncHookState';

type HookState = AsyncHookState<ErrorCodeType, Cart>;
type AsyncResult = AsyncRequestResult<ErrorCodeType, Cart>;

export class UseUpdateShippingPreferenceUnexpectedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UseUpdateShippingPreferenceUnexpectedError';
  }
}

export const useUpdateShippingPreference = ({
  client,
  dispatch,
}: {
  client: CartCheckoutClient;
  dispatch: StateDispatch;
}) => {
  const abortController = useRef<AbortController>();
  const [result, setResult] = useState<HookState>({});

  const handler = async (
    shippingMethod: ShippingMethod,
    shippingAddress?: CartQueryShippingAddress
  ): Promise<AsyncResult> => {
    // Abort any pending requests
    if (abortController.current) {
      abortController.current.abort();
    }

    const newAbortController = new AbortController();
    abortController.current = newAbortController;

    setResult((prev) => ({ ...prev, state: StateCode.LOADING }));
    dispatch({ type: DispatchActionsType.SET_CART_LOADING, payload: true });

    return client
      .updateShippingPreference(
        shippingMethod === ShippingMethod.Express ? 'express' : 'standard',
        newAbortController.signal
      )
      .then(async () => {
        return client
          .fetchCartAndUpdateShippingCountry(
            newAbortController.signal,
            shippingAddress
          )
          .then<AsyncResult>((res) => {
            dispatch({ type: DispatchActionsType.SET_CART, payload: res });
            return {
              data: res,
              state: StateCode.SUCCESS,
            };
          })
          .catch<AsyncResult>((err) => {
            const errMessage = err instanceof Error ? err.message : String(err);

            const fetchCartError =
              new UseUpdateShippingPreferenceUnexpectedError(
                `Unexpected error while fetching cart using useUpdateShippingPreference() "${errMessage}"`
              );

            logger.error(
              {
                err: fetchCartError,
                message: fetchCartError.message,
                context:
                  'CartAndDeliveryDatesDataContext/useUpdateShippingPreference',
              },
              fetchCartError.message
            );

            dispatch({
              type: DispatchActionsType.SET_CART_LOADING,
              payload: false,
            });

            return {
              data: undefined,
              state: StateCode.FAILED,
              errors: [
                {
                  code: ErrorCode.CART_FETCH_ERROR,
                  exception: err,
                },
              ],
            };
          });
      })
      .catch<AsyncResult>((err) => {
        const message =
          "Unexpected error while updating the cart's shipping preference using useUpdateShippingPreference()";

        logger.error(
          {
            err,
            message,
            context:
              'CartAndDeliveryDatesDataContext/useUpdateShippingPreference',
            params: shippingMethod,
          },
          message
        );

        dispatch({
          type: DispatchActionsType.SET_CART_LOADING,
          payload: false,
        });

        return {
          state: StateCode.FAILED,
          errors: [
            {
              code: ErrorCode.SHIPPING_PREFERENCE_UPDATE_ERROR,
              exception: err,
            },
          ],
        };
      })
      .then((r) => {
        setResult(r);
        return r;
      });
  };

  return {
    ...result,
    abortController,
    handler,
  };
};
